"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import { LaborResults } from "@/components/tools/econ-macro/labor-results";
import { MACRO_CHART_STRINGS } from "@/components/tools/econ-macro/line-figure";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type LaborStats,
  laborStats,
  type Rest,
} from "@/lib/tool-kit/econ-macro/labor";

/** English strings. A translated content file overrides these through `ui`. */
export const UNEMPLOYMENT_RATE_CALCULATOR_STRINGS = {
  ...MACRO_CHART_STRINGS,
  inputsHeading: "Your labor-market counts",
  restLabel: "Besides employed and unemployed, you have",
  restPopulation: "The civilian noninstitutional population aged 16 and over",
  restNotInLaborForce: "The number not in the labor force",
  unitLabel: "Counts are in",
  unitPeople: "People",
  unitThousand: "Thousands",
  unitMillion: "Millions",
  compareLabel: "Compare two periods",
  firstPeriod: "Earlier period",
  secondPeriod: "Later period",
  onePeriod: "Your figures",
  employed: "Employed",
  unemployed: "Unemployed",
  population: "Population aged 16 and over (civilian, noninstitutional)",
  notInLaborForce: "Not in the labor force",
  periodField: "{period}: {field}",
  inputsHint:
    "Employed and unemployed follow the BLS definitions, so do not count people who are not looking for work as unemployed. Use any one unit for every count.",
  invalidCounts: "Enter every count as a number of 0 or more.",
  emptyLaborForce:
    "The labor force is the employed plus the unemployed. With both at zero, every rate would divide by zero. Enter at least one person.",
  populationTooSmall:
    "The population cannot be smaller than the labor force (employed + unemployed). Check the population or the number not in the labor force.",
  resultsHeading: "Results",
  unemploymentRate: "Unemployment rate (U-3)",
  participationRate: "Labor force participation rate",
  employmentRatio: "Employment-population ratio",
  laborForce: "Labor force",
  populationShort: "Population aged 16 and over",
  measureColumn: "Measure",
  changeColumn: "Change",
  pointsSuffix: "{value} percentage points",
  measuresHeading: "All the measures",
  unitNote: "Counts are in {unit}.",
  workingHeading: "Working: {period}",
  workLaborForce: "Labor force = employed + unemployed = {e} + {u} = {lf}",
  workRest:
    "Not in the labor force = population − labor force = {pop} − {lf} = {nilf}",
  workPopulation:
    "Population = labor force + not in the labor force = {lf} + {nilf} = {pop}",
  workUnemployment:
    "Unemployment rate = unemployed ÷ labor force = {u} ÷ {lf} = {ur}",
  workParticipation:
    "Labor force participation rate = labor force ÷ population = {lf} ÷ {pop} = {lfpr}",
  workEmployment:
    "Employment-population ratio = employed ÷ population = {e} ÷ {pop} = {epop}",
  seriesEmployed: "Employed",
  seriesUnemployed: "Unemployed",
  seriesNotInLaborForce: "Not in the labor force",
  chartTitle: "Where the adult population stands",
  chartXTitle: "Number of people",
  chartYTitle: "Period",
  chartDescription:
    "A stacked bar chart splitting the population aged 16 and over into employed, unemployed and not in the labor force, for {count} period or periods.",
  figureNote:
    "The unemployment rate counts only people who are actively looking for work, so it can rise when more people start looking, even if employment is also rising.",
  definitions:
    "BLS definitions: a person is unemployed if they had no job in the survey week, were available for work and had looked for work in the past four weeks (or were waiting to be recalled from a layoff). The labor force counts the employed and unemployed only; everyone else aged 16 and over is not in the labor force. U-3 is the official rate. Broader rates, U-4 to U-6, add discouraged, marginally attached and involuntary part-time workers.",
} as const;

export type Strings = typeof UNEMPLOYMENT_RATE_CALCULATOR_STRINGS;
type Unit = "people" | "thousand" | "million";

interface Fields {
  employed: string;
  unemployed: string;
  third: string;
}

const FIRST: Record<Rest, Fields> = {
  population: { employed: "158", unemployed: "6", third: "262" },
  "not-in-labor-force": { employed: "158", unemployed: "6", third: "98" },
};
const SECOND: Record<Rest, Fields> = {
  population: { employed: "159.5", unemployed: "7.5", third: "263" },
  "not-in-labor-force": { employed: "159.5", unemployed: "7.5", third: "96" },
};

function solve(fields: Fields, rest: Rest, strings: Strings) {
  const e = parseNumberInput(fields.employed);
  const u = parseNumberInput(fields.unemployed);
  const third = parseNumberInput(fields.third);
  if (e === null || u === null || third === null) {
    return { stats: null, problem: strings.invalidCounts };
  }
  const outcome = laborStats(e, u, third, rest);
  if (outcome === "invalid") {
    return { stats: null, problem: strings.invalidCounts };
  }
  if (outcome === "empty-labor-force") {
    return { stats: null, problem: strings.emptyLaborForce };
  }
  if (outcome === "population-too-small") {
    return { stats: null, problem: strings.populationTooSmall };
  }
  return { stats: outcome, problem: null };
}

function PeriodFields({
  idPrefix,
  title,
  fields,
  setFields,
  rest,
  strings,
}: {
  idPrefix: string;
  title: string;
  fields: Fields;
  setFields: (fields: Fields) => void;
  rest: Rest;
  strings: Strings;
}) {
  const label = (field: string) =>
    title ? t(strings.periodField, { period: title, field }) : field;
  const set = (key: keyof Fields) => (value: string) =>
    setFields({ ...fields, [key]: value });
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <TextField
        id={`${idPrefix}-e`}
        label={label(strings.employed)}
        numeric
        onChange={set("employed")}
        value={fields.employed}
      />
      <TextField
        id={`${idPrefix}-u`}
        label={label(strings.unemployed)}
        numeric
        onChange={set("unemployed")}
        value={fields.unemployed}
      />
      <TextField
        id={`${idPrefix}-t`}
        label={label(
          rest === "population" ? strings.population : strings.notInLaborForce
        )}
        numeric
        onChange={set("third")}
        value={fields.third}
      />
    </div>
  );
}

export default function UnemploymentRateCalculatorWidget() {
  const strings = useToolUi(UNEMPLOYMENT_RATE_CALCULATOR_STRINGS);
  const [rest, setRest] = useState<Rest>("population");
  const [unit, setUnit] = useState<Unit>("million");
  const [compare, setCompare] = useState(false);
  const [first, setFirst] = useState(FIRST.population);
  const [second, setSecond] = useState(SECOND.population);

  const changeRest = (value: Rest) => {
    setRest(value);
    setFirst(FIRST[value]);
    setSecond(SECOND[value]);
  };
  const unitName = {
    people: strings.unitPeople,
    thousand: strings.unitThousand,
    million: strings.unitMillion,
  }[unit].toLowerCase();

  const one = solve(first, rest, strings);
  const two = compare ? solve(second, rest, strings) : null;
  const problem = one.problem ?? two?.problem ?? null;
  const periods: { name: string; stats: LaborStats }[] = [];
  if (!problem && one.stats) {
    periods.push({
      name: compare ? strings.firstPeriod : strings.onePeriod,
      stats: one.stats,
    });
    if (two?.stats) {
      periods.push({ name: strings.secondPeriod, stats: two.stats });
    }
  }

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.inputsHint}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="ur-rest"
            label={strings.restLabel}
            onChange={changeRest}
            options={[
              { value: "population", label: strings.restPopulation },
              {
                value: "not-in-labor-force",
                label: strings.restNotInLaborForce,
              },
            ]}
            value={rest}
          />
          <SelectField
            id="ur-unit"
            label={strings.unitLabel}
            onChange={setUnit}
            options={[
              { value: "people", label: strings.unitPeople },
              { value: "thousand", label: strings.unitThousand },
              { value: "million", label: strings.unitMillion },
            ]}
            value={unit}
          />
        </div>
        <Checkbox
          checked={compare}
          label={strings.compareLabel}
          onChange={setCompare}
        />
        <PeriodFields
          fields={first}
          idPrefix="ur-a"
          rest={rest}
          setFields={setFirst}
          strings={strings}
          title={compare ? strings.firstPeriod : ""}
        />
        {compare ? (
          <PeriodFields
            fields={second}
            idPrefix="ur-b"
            rest={rest}
            setFields={setSecond}
            strings={strings}
            title={strings.secondPeriod}
          />
        ) : null}
      </section>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
        {periods.length > 0 ? (
          <LaborResults
            periods={periods}
            rest={rest}
            strings={strings}
            unitName={unitName}
          />
        ) : null}
      </section>
    </div>
  );
}
